import React from 'react';
import { Phone, Mail, Instagram, Facebook, MessageCircle, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/sections/Footer';
import Seo from '../components/Seo';
import { businessInfo, locations } from '../mock';
import { SITE_URL, getBreadcrumbJsonLd } from '../lib/structuredData';

const phoneDigits = String(businessInfo.phone || '').replace(/[^\d]/g, '');

const Contacto = () => {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${SITE_URL}/contacto#page`,
      name: 'Contacto — Brazolovers',
      url: `${SITE_URL}/contacto/`,
    },
    getBreadcrumbJsonLd([
      { name: 'Inicio', path: '/' },
      { name: 'Contacto', path: '/contacto/' },
    ]),
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Seo
        title="Contacto Brazolovers | Pedidos y atención en Quito"
        description="Contáctanos para pedir brazos gitanos, pasteles y más en Quito. Escríbenos por WhatsApp, llámanos o visita nuestros dos locales. Pedidos a domicilio y para retiro en tienda."
        path="/contacto/"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="container mx-auto px-4 md:px-6 py-10 flex-1">
        <h1 className="text-3xl md:text-4xl font-bold text-chocolate mb-2">Contacto</h1>
        <p className="text-chocolate-light mb-8 max-w-2xl">
          ¿Quieres hacer un pedido o tienes alguna pregunta? Estos son todos los canales
          para comunicarte con Brazolovers en Quito.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <section className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
            <h2 className="text-2xl font-bold text-chocolate">Escríbenos</h2>

            <a
              href={`https://wa.me/${phoneDigits}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-chocolate hover:text-caramel"
            >
              <MessageCircle className="w-5 h-5 text-caramel" />
              WhatsApp: +{phoneDigits}
            </a>
            <a href={`tel:+${phoneDigits}`} className="flex items-center gap-3 text-chocolate hover:text-caramel">
              <Phone className="w-5 h-5 text-caramel" />
              Llámanos: +{phoneDigits}
            </a>
            <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-3 text-chocolate hover:text-caramel">
              <Mail className="w-5 h-5 text-caramel" />
              {businessInfo.email}
            </a>

            <div className="flex items-center gap-4 pt-2">
              <a href={businessInfo.social.instagram} target="_blank" rel="noopener noreferrer" className="text-chocolate hover:text-caramel">
                <Instagram className="w-6 h-6" />
              </a>
              <a href={businessInfo.social.facebook} target="_blank" rel="noopener noreferrer" className="text-chocolate hover:text-caramel">
                <Facebook className="w-6 h-6" />
              </a>
            </div>
          </section>

          <section className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
            <h2 className="text-2xl font-bold text-chocolate">Cómo pedir</h2>
            <ol className="list-decimal list-inside space-y-2 text-chocolate-light">
              <li>Revisa el <Link to="/menu/" className="text-caramel font-medium hover:underline">menú completo</Link> y elige tus productos.</li>
              <li>Haz tu pedido en línea desde la <Link to="/" className="text-caramel font-medium hover:underline">página principal</Link> o escríbenos por WhatsApp.</li>
              <li>Elige <strong>retiro en tienda</strong> en cualquiera de nuestros locales o <strong>entrega a domicilio</strong>.</li>
            </ol>

            <h3 className="text-lg font-semibold text-chocolate pt-2">Nuestros locales</h3>
            <ul className="space-y-2 text-chocolate-light">
              {locations.map((loc) => (
                <li key={loc.id} className="flex items-start gap-2">
                  <MapPin className="w-5 h-5 mt-0.5 shrink-0 text-caramel" />
                  <span>
                    <strong>{loc.name}:</strong> {loc.address}
                  </span>
                </li>
              ))}
            </ul>
            <Link to="/ubicaciones/" className="inline-block text-caramel font-medium hover:underline">
              Ver ubicaciones y horarios →
            </Link>
          </section>
        </div>
      </main>

      <Footer businessInfo={businessInfo} />
    </div>
  );
};

export default Contacto;
